import InfiniteScroll from "react-infinite-scroll-component";
import { t } from "@/i18n-js/instance";
import { SearchInput } from "@/react/components/Modals/MessagingModal/LeftSidebar/List/SearchInput";
import { chatInboxPath } from "@circle-react/helpers/urlHelpers";
import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import { useDebouncedValue } from "@circle-react/hooks/utils/useDebouncedValue";
import type { Chat } from "@circle-react/types/CommunityInbox/Chat";
import { Loader } from "@circle-react-uikit/Loader";
import { ChatItem } from "./ChatList/ChatItem";
import { EmptyChat } from "./ChatList/EmptyChat";
import { useCommunityInboxSearch } from "./hooks/useCommunityInboxSearch";

export interface CommunityInboxChatSearchProps {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  setIsSearchBarVisible: (value: boolean) => void;
}

export const CommunityInboxChatSearch = ({
  searchQuery,
  setSearchQuery,
  setIsSearchBarVisible,
}: CommunityInboxChatSearchProps) => {
  const debouncedValue = useDebouncedValue(searchQuery, 300);

  const { isLoading, fetchNextPage, hasNextPage } =
    useCommunityInboxSearch(debouncedValue);

  const { adminChats: chats, currentChatId } = useCommunityInboxStore();

  const onClickCancel = () => {
    setIsSearchBarVisible(false);
    setSearchQuery("");
  };

  const handleOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!event.shiftKey && event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
    <>
      <div className="border-primary my-1 flex h-10 w-full items-center justify-center">
        <SearchInput
          searchValue={searchQuery}
          onChange={setSearchQuery}
          onKeyPress={handleOnEnter}
          onClickCancel={onClickCancel}
          placeholder={t("community_inbox.header.placeholder")}
          className="!h-[34px] duration-500 ease-in"
          showCancelButton
          shouldAutoFocus
        />
      </div>
      {searchQuery &&
        (isLoading ? (
          <div className="h-auto overflow-auto p-2">
            <Loader center />
          </div>
        ) : (
          <>
            {chats.length == 0 && debouncedValue.length > 0 && <EmptyChat />}
            <InfiniteScroll
              className="!h-[calc(100dvh-160px)] overflow-auto px-2"
              scrollThreshold={0.8}
              next={fetchNextPage}
              hasMore={Boolean(hasNextPage)}
              dataLength={chats.length}
              loader={<Loader center />}
              height="100%"
            >
              {chats.map((chat: Chat) => {
                const isOpenChatRoom = chat.id == currentChatId;
                return (
                  <ChatItem
                    key={chat.id}
                    chat={chat}
                    path={chatInboxPath({ id: chat.id.toString() })}
                    isUnread={
                      !isOpenChatRoom && chat.has_unread_messages_for_admin
                    }
                  />
                );
              })}
            </InfiniteScroll>
          </>
        ))}
    </>
  );
};
